/**
 * LP-5358: the admin portal's single boundary onto the supplier-allocation balance-snapshot
 * contract defined by LP-5352 (merged to `dev` as `4435c9120`).
 *
 * <p>Everything the portal believes about the SHAPE of that contract lives in this file. The
 * component and template consume only {@link AllocationBalanceRow}, so a future change to the DTO
 * is confined to the field reads in {@link toAllocationBalanceRow} and
 * {@link resolveParentBalanceApplicability} -- no template, component or test rewiring.
 *
 * <p><b>Why allocation-level at all.</b> Before supplier allocations existed, the parent
 * transaction WAS the financial movement, so a parent-level before/after pair described it
 * correctly. For allocation-backed cashback the movement happens on each supplier sub-ledger leg:
 * legs hit different sub-ledgers, succeed/fail/stay pending independently, and settle in one shared
 * blockchain batch or in separate later ones. One aggregate parent pair cannot describe that, so
 * balances are rendered per allocation and the parent pair is only shown where it is real.
 *
 * <p><b>No portal arithmetic.</b> Nothing here adds, subtracts, carries forward or infers a
 * balance. A value is rendered only if the backend sent it; absence renders as unavailable. That
 * single rule is what satisfies LP-5358's per-status requirements, because LP-5352 writes a
 * snapshot row only where a real mutation was observed on both sides:
 *
 * <ul>
 *   <li>{@code SUCCESS} -- the row exists, so both authoritative values render.
 *   <li>{@code PENDING}/{@code SUBMITTED}/{@code UNKNOWN} -- only what exists renders; because the
 *       portal never computes, a final after-value can never be inferred for a leg that has not
 *       settled.
 *   <li>{@code FAILED} -- a row exists only where a real mutation occurred, so a failure that moved
 *       nothing renders unavailable rather than a misleading pair.
 *   <li>Historical legs with no snapshot -- no row, so unavailable.
 * </ul>
 *
 * The portal deliberately does NOT suppress values by status on top of that. Suppressing a value
 * the backend called authoritative would be the portal overriding the ledger, which is the same
 * class of mistake as fabricating one.
 */

/**
 * A balance that is either authoritative data from the backend or genuinely absent.
 *
 * <p>Modelled as a discriminated union rather than `number | null` so that "unavailable" cannot be
 * accidentally coerced, compared or formatted into `0.00` by a template expression. Distinguishing
 * a real authoritative zero from an unknown balance is the central correctness requirement of
 * LP-5358, and a nullable number makes that distinction one stray `|| 0` away from being lost.
 */
export type BalanceSnapshot =
    | { readonly state: 'available'; readonly amount: number }
    | { readonly state: 'unavailable' };

export const UNAVAILABLE_BALANCE: BalanceSnapshot = { state: 'unavailable' };

/** The marker the portal renders for a value the backend does not have. Never `0.00`. */
export const UNAVAILABLE_DISPLAY = '—';

/**
 * The allocation-level snapshot fields, CONFIRMED by LP-5352 (merged to `dev` as `4435c9120`).
 *
 * <p>`CashbackViewModel.RMSCashbackAllocation` now carries these as a direct, unmodified read of
 * `supplier_balance_snapshot`; nothing is computed, aggregated or defaulted backend-side. They stay
 * optional and nullable here because null is MEANINGFUL and common: it means no snapshot row exists
 * for this leg, so no mutation was ever observed -- a withheld/PENDING leg, or an allocation
 * predating LP-5352. The backend's own contract note is explicit that a client must render that as
 * unavailable and never as `0.00`, which would assert the sub-ledger was empty.
 *
 * <p>Typed as `number | string` because the backend serializes these as `BigDecimal` (deliberately,
 * so the API reproduces the persisted value exactly rather than routing it through a `Double`), and
 * a BigDecimal may reach the client as a JSON number or as a string depending on the mapper's
 * configuration. {@link readBalanceSnapshot} normalizes both.
 */
export interface AllocationSnapshotFields {
    /** `supplier_entitlement_balance.available_amount` immediately BEFORE this leg's mutation. */
    balanceBefore?: number | string | null;

    /** The committed `available_amount` immediately after it. Never derived from `balanceBefore`. */
    balanceAfter?: number | string | null;

    /**
     * Which movement the balances describe: `EARN`, `RESERVE` or `RELEASE`.
     *
     * <p>Not rendered, and deliberately not used to filter: the backend already restricts this list
     * to the allocation's own `EARN` credit (`findByCashbackAllocationIdInAndMutationType`), so
     * every row here is an EARN. Carried so the contract is complete at the boundary, and so a
     * future reader can see that selecting the right movement is the backend's job, not a
     * correction the portal should attempt.
     */
    mutationType?: string | null;

    /**
     * Blockchain provenance OF THE SNAPSHOT -- how the leg executed, as recorded at mutation time.
     *
     * <p>Distinct from {@link AllocationBalanceSource.liskId}, which is the allocation's own
     * explorer URL and remains what the Blockchain column links to. Kept raw and unrendered: LP-5358
     * requires preserving the authoritative link the portal already uses, not replacing it with a
     * second, differently-sourced hash.
     */
    txHash?: string | null;

    txNetwork?: string | null;
}

/** The shape {@link toAllocationBalanceRow} reads. */
export interface AllocationBalanceSource extends AllocationSnapshotFields {
    supplierName?: string | null;
    supplierId?: number | null;
    amount?: number | string | null;
    status?: string | null;

    /**
     * Execution/batch identity. Confirmed and live today: the portal mapper
     * (`TransactionListServiceImpl#stitchCashbackAllocations`) already populates it from
     * `cashback_allocation.cashback_execution_id`.
     *
     * <p>Several legs may legitimately share one execution, and legs of one parent transaction may
     * settle under different executions after a retry. The portal renders the identifier as given
     * and never groups, merges or de-duplicates rows by it.
     */
    executionId?: number | string | null;

    /**
     * The authoritative blockchain link for this leg. Despite the legacy name this is a fully
     * composed explorer URL, not a raw hash: the backend resolves it per allocation from
     * `tx_network` + (`tx_hash` ?? `lisk_tx_id`) and sends null when provenance is unknown.
     * Preserved verbatim -- the portal neither rebuilds nor re-networks it.
     *
     * <p>Two legs executed in the same batch share a hash and therefore share this URL. That is
     * displayed as-is on both rows; the portal does not infer batch grouping from it.
     */
    liskId?: string | null;
}

/** One allocation leg, resolved for rendering. */
export interface AllocationBalanceRow {
    readonly supplierName: string;
    readonly supplierId: number | null;
    readonly amount: BalanceSnapshot;
    readonly status: string;
    readonly balanceBefore: BalanceSnapshot;
    readonly balanceAfter: BalanceSnapshot;
    readonly executionRef: string | null;
    readonly blockchainUrl: string | null;
}

/**
 * Resolves one raw backend value into an authoritative amount or an explicit absence.
 *
 * <p>Deliberately not falsiness-based: `0` is a legitimate authoritative balance and must resolve
 * to `available`, while `null`, `undefined`, `''`, whitespace and non-numeric text must all resolve
 * to `unavailable`. `Number('')` being `0` is exactly the trap this guards.
 *
 * <p>Numeric strings are accepted because a `BigDecimal(19,4)` may serialize as a JSON number or as
 * a string depending on the mapper's configuration. Parsing both normalizes a value the backend
 * sent; it never invents one it did not.
 *
 * <p><b>On IEEE-754 precision</b> (Codex review, PR #109, P2). A `BigDecimal(19,4)` can in principle
 * hold more significant digits than a double represents exactly, so routing it through `Number` can
 * alter it. Deliberately accepted rather than fixed with a decimal formatter:
 *
 * <ul>
 *   <li>UserService sets no `WRITE_NUMBERS_AS_STRINGS`/`ToStringSerializer`, so Jackson serializes
 *       these as JSON <i>numbers</i>. `JSON.parse` has therefore already produced a double before
 *       this function is reached -- the `Number` call here is not the lossy step in the real path,
 *       and no portal-side formatter could recover precision that never arrived.
 *   <li>Balances render at two decimal places. A double's relative precision is ~2.2e-16, so an
 *       absolute error large enough to change the rendered value (> 0.005) needs a magnitude above
 *       roughly 2.3e13. These are per-user, per-supplier cashback sub-ledger balances in ZAR; the
 *       threshold is some ten orders of magnitude beyond anything reachable.
 *   <li>The alternative -- string-based rounding and separator insertion -- would put real
 *       arithmetic in the portal to defend against an unreachable case, which is the opposite of
 *       what LP-5358 is for.
 * </ul>
 *
 * If balances ever become high-magnitude or non-ZAR, revisit this before the display does.
 */
export function readBalanceSnapshot(raw: unknown): BalanceSnapshot {
    if (typeof raw === 'number') {
        return Number.isFinite(raw) ? { state: 'available', amount: raw } : UNAVAILABLE_BALANCE;
    }

    if (typeof raw === 'string') {
        const trimmed = raw.trim();

        if (trimmed === '') {
            return UNAVAILABLE_BALANCE;
        }

        const parsed = Number(trimmed);
        return Number.isFinite(parsed)
            ? { state: 'available', amount: parsed }
            : UNAVAILABLE_BALANCE;
    }

    return UNAVAILABLE_BALANCE;
}

export function isBalanceAvailable(snapshot: BalanceSnapshot | null | undefined): boolean {
    return snapshot?.state === 'available';
}

/** The numeric value for the `number` pipe, or null when there is nothing authoritative to show. */
export function balanceAmount(snapshot: BalanceSnapshot | null | undefined): number | null {
    return snapshot?.state === 'available' ? snapshot.amount : null;
}

/**
 * An identifier the backend sent, as an opaque display string, or null.
 *
 * <p>Kept opaque on purpose: an execution/batch reference is an identity, not a quantity, so it is
 * never parsed, padded or reformatted. `0` is preserved as a real identifier rather than dropped by
 * falsiness.
 */
function readIdentifier(raw: unknown): string | null {
    if (typeof raw === 'number') {
        return Number.isFinite(raw) ? String(raw) : null;
    }

    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        return trimmed === '' ? null : trimmed;
    }

    return null;
}

/**
 * The single point at which allocation API field names are read.
 *
 * <p>Tolerant of a partially-populated leg by construction: every field resolves independently, so
 * a leg carrying a before-value but no after-value renders exactly that, and a leg carrying neither
 * renders unavailable on both. There is no all-or-nothing gate and no cross-field inference.
 */
export function toAllocationBalanceRow(
    allocation: AllocationBalanceSource | null | undefined,
): AllocationBalanceRow {
    return {
        supplierName: allocation?.supplierName?.trim() || '',
        supplierId: typeof allocation?.supplierId === 'number' ? allocation.supplierId : null,
        amount: readBalanceSnapshot(allocation?.amount),
        status: allocation?.status?.trim() || '',
        balanceBefore: readBalanceSnapshot(allocation?.balanceBefore),
        balanceAfter: readBalanceSnapshot(allocation?.balanceAfter),
        executionRef: readIdentifier(allocation?.executionId),
        blockchainUrl: readIdentifier(allocation?.liskId),
    };
}

export function toAllocationBalanceRows(
    allocations: ReadonlyArray<AllocationBalanceSource> | null | undefined,
): AllocationBalanceRow[] {
    return (allocations || []).map((allocation) => toAllocationBalanceRow(allocation));
}

/**
 * LP-5358: one supplier leg of an RMS cashback REDEMPTION, as the admin API now sends it on
 * `supplierCashbacksUsed`.
 *
 * <p>Distinct from {@link AllocationBalanceSource} because the two describe opposite movements and
 * carry different fields. An EARN allocation credits the trader and has a status, an execution and
 * its own explorer link; a redemption leg debits the trader, has no per-leg status (the batch is
 * captured as a whole) and no per-leg chain link, and instead carries `mutationType`, which is the
 * field that distinguishes a reservation that stuck from one that was released.
 */
export interface RedemptionLegSource {
    readonly supplierId?: number | null;
    readonly supplierName?: string | null;
    readonly amount?: number | string | null;
    readonly balanceBefore?: number | string | null;
    readonly balanceAfter?: number | string | null;
    readonly mutationType?: string | null;
}

/** LP-5358: a redemption leg resolved for rendering. */
export interface RedemptionLegRow {
    readonly supplierName: string;
    readonly supplierId: number | null;
    readonly amountUsed: BalanceSnapshot;
    readonly balanceBefore: BalanceSnapshot;
    readonly balanceAfter: BalanceSnapshot;
    readonly mutationType: string | null;
}

/**
 * LP-5358: resolves one redemption leg, preserving absence exactly as the allocation path does.
 *
 * <p>`mutationType` is carried through verbatim and *not* interpreted. The backend already selects
 * the movement to show — the latest `RESERVE` per owner reference — so the portal neither picks
 * between rows nor nets a `RELEASE` against a `RESERVE`. Both remain distinct states in the ledger
 * and the portal's job is to say which one it was given, because a released reservation means the
 * cashback was returned rather than permanently spent.
 *
 * <p>No arithmetic here either: a redemption consuming several supplier balances renders as several
 * legs, never as a sum. Summing independent sub-ledgers would be fabricated accounting.
 */
export function toRedemptionLegRow(leg: RedemptionLegSource | null | undefined): RedemptionLegRow {
    return {
        supplierName: leg?.supplierName?.trim() || '',
        supplierId: typeof leg?.supplierId === 'number' ? leg.supplierId : null,
        amountUsed: readBalanceSnapshot(leg?.amount),
        balanceBefore: readBalanceSnapshot(leg?.balanceBefore),
        balanceAfter: readBalanceSnapshot(leg?.balanceAfter),
        mutationType: readIdentifier(leg?.mutationType),
    };
}

export function toRedemptionLegRows(
    legs: ReadonlyArray<RedemptionLegSource> | null | undefined,
): RedemptionLegRow[] {
    return (legs || []).map((leg) => toRedemptionLegRow(leg));
}

/**
 * Whether the PARENT transaction's Sender/Receiver before/after pair describes a real
 * parent-level wallet movement.
 *
 * <ul>
 *   <li>{@code applicable} -- the backend confirms a parent wallet movement; render as always.
 *   <li>{@code not-applicable} -- the backend confirms there was none, so the parent pair would be
 *       a misleading aggregate of movements that actually happened per allocation. Render the
 *       portal's established `NA` instead.
 *   <li>{@code unknown} -- no signal. Legacy rendering is preserved unchanged.
 * </ul>
 */
export type ParentBalanceApplicability = 'applicable' | 'not-applicable' | 'unknown';

/**
 * An EXPLICIT backend statement about the parent transaction.
 *
 * <p><b>LP-5352 did not add such a field, and that is a settled outcome rather than a gap.</b> The
 * backend expresses "this parent carried no wallet movement" by ABSENCE instead: it stopped writing
 * the hardcoded `0.0` after-values into `transaction_history_extras`, `normalizeBalance` now
 * preserves null rather than coercing to `0.0`, and `TransactionViewPortalModel.formatBalanceOrNull`
 * returns null rather than `"0.00"` -- whose own contract note states that the portal must render
 * that as unavailable. An allocation-backed cashback therefore arrives with null parent balances,
 * which this portal already renders as the unavailable marker, and the per-leg truth is on the
 * allocation snapshots.
 *
 * <p>So the flag below is not dead weight and not a stale provisional: it is the ONE path by which
 * the portal can ever distinguish "not applicable -- the accounting happened per allocation" from
 * "unknown -- we simply do not have this value". Those read identically today, both as an unavailable
 * marker, which is honest but less informative than `NA` with its explanation. The reader stays so
 * that a later contract can say so explicitly without the portal having to guess.
 *
 * <p>Guessing is what must never happen. The presence of supplier allocations does NOT imply the
 * parent had no wallet movement -- a marketplace purchase that earns allocation-backed cashback has
 * a perfectly real customer-to-merchant wallet leg whose parent before/after values are correct and
 * must keep rendering. Only the backend knows, so the portal waits to be told rather than inferring
 * from transaction type or from allocations existing.
 */
export interface ParentBalanceSignalFields {
    /** True when the parent transaction itself carried a wallet-level financial movement. */
    parentWalletMovement?: boolean | null;
}

/**
 * Resolves the parent pair's applicability from the backend signal alone.
 *
 * <p>Resolves to `unknown` for every transaction the LP-5352 contract produces, because that
 * contract carries no such signal -- so the legacy rendering is preserved, and a parent value that
 * is genuinely absent falls through to the unavailable marker rather than to a fabricated `0.00`.
 * That is what keeps historical and legacy wallet-level CZAR transactions working untouched.
 */
export function resolveParentBalanceApplicability(
    transaction: ParentBalanceSignalFields | null | undefined,
): ParentBalanceApplicability {
    const signal = transaction?.parentWalletMovement;

    if (signal === true) {
        return 'applicable';
    }

    if (signal === false) {
        return 'not-applicable';
    }

    return 'unknown';
}

/**
 * A parent-level balance string, kept OPAQUE.
 *
 * <p>Unlike allocation snapshots these are not re-formatted. The legacy parent fields are typed as
 * strings and have been rendered verbatim for years; running them through a number pipe would
 * silently change the display of every historical transaction, and would turn any pre-formatted
 * value the backend happens to send into an `—`. So a non-empty value is passed through exactly as
 * received -- including an authoritative `"0"` or `"0.00"` -- and only a genuinely empty one
 * becomes unavailable.
 */
export function readParentBalanceDisplay(raw: unknown): string | null {
    if (typeof raw === 'number') {
        return Number.isFinite(raw) ? String(raw) : null;
    }

    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        return trimmed === '' ? null : trimmed;
    }

    return null;
}
